import { useState } from "react";

import { useTableSearch } from "@/utils";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useGetCompanyQuery } from "@/pages/Private/redux/companies/companies.api";
import { useGetAdminFeaturesQuery } from "@/pages/Private/redux/feature/admin-feature.api";
import {
	useGetStaffAssigneesQuery,
	useGetStaffTicketsQuery,
	useGetStaffTicketStatsQuery,
} from "@/pages/Private/redux/tickets/tickets-admin.api";

import {
	buildStaffQueueArg,
	EMPTY_STAFF_FILTERS,
	staffFiltersEqual,
	StaffQueueFilters,
} from "../utils/staffQueueQuery";
import { staffTabCounts, StaffTicketTab } from "../utils/staffTicketTabs";
import { isSupportUiActive } from "../utils/supportUiActive";
import { StaffTicketQueueController } from "./staffTicketQueueController";

/**
 * The staff queue's state (PREC-3939). Server-driven: tab + filter row + paging become the
 * `tickets/admin` arg through `buildStaffQueueArg` and the page is rendered as it comes back.
 * `useTableSearch` (the Support Dashboard's helper) owns the debounced search and the URL-synced
 * page / limit, starting at the remembered page size (PREC-4403); `?companyId=` from a Support Dashboard link seeds the company filter. The strip,
 * the tab counts and the UI-off marker read their own queries and are never narrowed by the
 * filter row (decision 5).
 */
export const useStaffTicketQueue = (): StaffTicketQueueController => {
	const { user } = useAppSelector(authSelector);
	const { page, setPage, limit, setLimit, searchValue, setSearchValue, query, searchParams } =
		useTableSearch();

	const [tabState, setTabState] = useState(StaffTicketTab.NEEDS_ACTION);
	const [filtersState, setFiltersState] = useState<StaffQueueFilters>(() => ({
		...EMPTY_STAFF_FILTERS,
		companyId: Number(searchParams.get("companyId")) || null,
	}));

	const currentUserId = user?.id ?? null;
	const arg = buildStaffQueueArg({
		tab: tabState,
		filters: filtersState,
		search: query,
		page,
		limit,
		currentUserId,
	});

	const { data, isLoading, isFetching } = useGetStaffTicketsQuery(arg);
	const { data: stats } = useGetStaffTicketStatsQuery();
	const { data: assignees } = useGetStaffAssigneesQuery();
	const { data: adminFeatures } = useGetAdminFeaturesQuery();
	// A company seeded from the URL comes as an id only; its name is one lookup away.
	const { data: seededCompany } = useGetCompanyQuery(filtersState.companyId ?? 0, {
		skip: !filtersState.companyId || filtersState.companyName !== null,
	});

	const setTab = (next: StaffTicketTab) => {
		setTabState(next);
		// The status options are the tab's own, so a selection cannot carry over.
		setFiltersState((current) => ({ ...current, statuses: [] }));
		setPage(1);
	};

	const setFilters = (patch: Partial<StaffQueueFilters>) => {
		const next = { ...filtersState, ...patch };

		// The shared Dropdown re-selects its value from an effect on every render: a patch that
		// changes nothing must not produce a new state object, or the page renders forever.
		if (staffFiltersEqual(filtersState, next)) {
			return;
		}

		setFiltersState(next);
		setPage(1);
	};

	// meta.itemCount is a SQL count: a string at runtime (web CLAUDE.md §6).
	const itemCount = Number(data?.meta.itemCount ?? 0) || 0;

	const filters = filtersState;

	return {
		tab: tabState,
		setTab,
		filters,
		setFilters,
		search: searchValue,
		setSearch: setSearchValue,
		page,
		setPage,
		limit,
		setLimit,
		rows: data?.data ?? [],
		itemCount,
		totalPages: Math.max(1, Math.ceil(itemCount / limit)),
		isLoading,
		isFetching,
		stats,
		tabCounts: staffTabCounts(stats),
		assignees: assignees ?? [],
		currentUserId,
		companyLabel: filters.companyId
			? (filters.companyName ?? seededCompany?.name ?? `#${filters.companyId}`)
			: null,
		hasFilters: Boolean(
			query.trim() ||
			filters.statuses.length ||
			filters.assignee !== "ANY" ||
			filters.category ||
			filters.priority ||
			filters.companyId
		),
		isSupportUiActive: (companyId: number) => isSupportUiActive(adminFeatures, companyId),
	};
};
